/**
 * Messages from the sandbox admin (news, maintenance windows, incidents, information), kept by the sandbox gateway
 * (vfs-service src/gateway/messages) under the vfs path the web app already uses, with the person's authorized fetch:
 * `GET /api/v1/me/messages` -> `{ items, unread }`, `POST /api/v1/me/messages/read` `{ ids } | { all: true }`,
 * `POST /api/v1/me/messages/:id/dismiss` (the banner, until the message changes).
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');

async function call(path, { method = 'GET', body } = {}) {
  if (!base()) { return null; }
  const response = await getAuthorizedFetch()(`${base()}/api/v1/me/messages${path}`, {
    method,
    // Polled live (hooks/useSandboxMessages.js): never an answer from the browser's cache.
    ...(method === 'GET' ? { cache: 'no-store' } : {}),
    headers: { Accept: 'application/json', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  // A gateway without messages (404) or not reachable: nothing to show.
  if (response.status === 404) { return null; }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data;
}

/** `{ items, unread }`, or null where the gateway has no messages. */
export function getMessages() {
  return call('');
}

export function markMessagesRead({ ids, all } = {}) {
  return call('/read', { method: 'POST', body: all ? { all: true } : { ids: ids || [] } });
}

export function dismissMessage(id) {
  return call(`/${encodeURIComponent(id)}/dismiss`, { method: 'POST' });
}
